<template>
  <ODialog
    ref="dialog"
    :title="$t('components.product.actions.delete.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Product } from '~/_api/models/product'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref()
const product = ref<Product | null>(null)

const info = computed(() => {
  return $i18n.t('components.product.actions.delete.info', {
    name: product.value?.name,
    id: product.value?.idProduct,
  })
})

const open = (productItem: Product) => {
  product.value = productItem
  dialog.value.open()
}

const confirm = async () => {
  if (!product.value) return

  emit('update:loading', true)

  try {
    const response = await $api.product.delete(product.value)
    emit('update:loading', false)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.product.actions.delete.success'),
        text: $i18n.t('components.product.actions.delete.successInfo', { name: product.value.name }),
      })

      emit('success')

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('components.product.actions.delete.error'),
      text: $i18n.t('components.product.actions.delete.errorInfo', { name: product.value?.name }),
    })

    emit('error')
  }
}

defineExpose({
  open,
})
</script>
